/**
 * @file OrderRegistryCells.tsx
 * @description Ячейки реестра заказов, которым нужна своя разметка
 * (phase19.31, макет (а)): вторая линия «Стадии» и «Оплата». Что в них
 * писать, решают чистые функции `lib/order-registry-cells.ts`, здесь — только
 * вид (тон — цветами статусов темы).
 */

import { TriangleAlert } from "lucide-react";

import type { OrderDto } from "../../../lib/api-orders";
import { orderPaymentCell, orderStageNote } from "../../../lib/order-registry-cells";
import type { StatusTone } from "../../../lib/shop/status-meta";
import { cn } from "@/lib/utils";

/** Цвет основной линии «Оплаты» по тону: «Оплачен» — зелёный, возврат — красный. */
const PAYMENT_TONE_CLASS: Partial<Record<StatusTone, string>> = {
  success: "text-[var(--status-success-fg,var(--success))]",
  attention: "text-[var(--status-attention-fg,var(--warning))]",
  error: "text-[var(--status-error-fg,var(--destructive))]",
};

/** Вторая линия «Стадии»: проблема красным со значком, иначе — согласование словами. */
export function OrderStageNote({ order }: { order: OrderDto }) {
  const note = orderStageNote(order);
  if (!note) return null;
  if (!note.problem) return <>{note.text}</>;
  return (
    <span className="inline-flex min-w-0 items-center gap-1 text-[var(--status-error-fg,var(--destructive))]">
      <TriangleAlert className="size-3.5 shrink-0" aria-hidden />
      <span className="truncate">{note.text}</span>
    </span>
  );
}

/** «Оплата» в две линии; на 1024–1279 вторую линию занимает свёрнутая «Доставка». */
export function OrderPaymentCell({ order }: { order: OrderDto }) {
  const cell = orderPaymentCell(order);
  const toneClass = (cell.tone && PAYMENT_TONE_CLASS[cell.tone]) || "text-on-surface";
  return (
    <span className="flex min-w-0 flex-col">
      <span className={cn("type-body truncate", toneClass)}>{cell.primary}</span>
      {cell.secondary ? (
        <span className="type-caption hidden truncate text-on-surface-variant xl:block">{cell.secondary}</span>
      ) : null}
    </span>
  );
}
